import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Grid2X2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { QuickHeader } from "../components/QuickHeader";
import { ProductCard } from "../components/ProductCard";
import { StoreClosedNotice } from "../components/StoreClosedNotice";
import { EnableNotificationsCard } from "../components/EnableNotificationsCard";
import { useProducts, useCategories, useBusinessInfo } from "../hooks/useCatalog";

const categoryColours = ["#dcfce7", "#fef3c7", "#dbeafe", "#fce7f3"];

export default function QuickCommerceHome() {
  const { data: products = [], isLoading: productsLoading } = useProducts();
  const { data: categories = [], isLoading: categoriesLoading } = useCategories();
  const { data: businessInfo = {} } = useBusinessInfo();
  const [bannerIndex, setBannerIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(8);
  const touchStart = useRef(null);
  const loadMoreRef = useRef(null);

  const banners = useMemo(
    () =>
      (businessInfo.banners || [])
        .filter((banner) => banner.active !== false && banner.image)
        .sort((first, second) => (first.order || 0) - (second.order || 0)),
    [businessInfo.banners],
  );
  const offer = businessInfo.offer || {};

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setBannerIndex((current) => (current + 1) % banners.length);
    }, 3500);
    return () => window.clearInterval(timer);
  }, [banners.length]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisibleCount((count) => count + 8);
      },
      { rootMargin: "400px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [products.length, visibleCount]);

  const productsForCategory = (key) => products.find((product) => product.category === key);
  const copyOffer = async () => {
    try {
      await navigator.clipboard.writeText(offer.code || "");
      toast.success("Coupon copied");
    } catch {
      toast.error("Could not copy the coupon");
    }
  };
  const swipe = (event) => {
    if (touchStart.current === null || banners.length < 2) return;
    const difference = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(difference) > 35) {
      setBannerIndex(
        (current) => (current + (difference < 0 ? 1 : banners.length - 1)) % banners.length,
      );
    }
    touchStart.current = null;
  };

  return (
    <main className="min-h-screen bg-slate-50 pb-28">
      <QuickHeader businessInfo={businessInfo} />
      <StoreClosedNotice status={businessInfo.store_status} />
      <div className="mx-auto max-w-xl">
        <div className="px-3 pt-3">
          <EnableNotificationsCard />
        </div>
        {banners.length > 0 && (
          <section className="px-3 pt-3" data-testid="quick-banner-carousel">
            <Link
              to={banners[bannerIndex].link || "/c/all"}
              onTouchStart={(event) => {
                touchStart.current = event.touches[0].clientX;
              }}
              onTouchEnd={swipe}
              data-testid={`quick-banner-${bannerIndex}`}
              className="relative block aspect-[16/7] overflow-hidden rounded-2xl bg-emerald-100"
            >
              <img
                src={banners[bannerIndex].image}
                alt={banners[bannerIndex].title || "Quick Door Now offer"}
                width={1280}
                height={560}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover"
              />
              {(banners[bannerIndex].title || banners[bannerIndex].subtitle) && (
                <div className="absolute inset-x-0 bottom-0 bg-slate-950/55 p-3 text-white">
                  <p className="text-sm font-extrabold">{banners[bannerIndex].title}</p>
                  <p className="text-xs">{banners[bannerIndex].subtitle}</p>
                </div>
              )}
            </Link>
            {banners.length > 1 && (
              <div className="mt-2 flex justify-center gap-1.5" data-testid="quick-banner-dots">
                {banners.map((banner, index) => (
                  <button
                    type="button"
                    key={banner.id || index}
                    onClick={() => setBannerIndex(index)}
                    data-testid={`quick-banner-dot-${index}`}
                    aria-label={`Show banner ${index + 1}`}
                    className={`h-1.5 rounded-full ${index === bannerIndex ? "w-5 bg-emerald-700" : "w-1.5 bg-slate-300"}`}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {offer.enabled && offer.code && (
          <section
            className="mx-3 mt-3 flex items-center gap-3 border border-amber-300 bg-amber-100 p-3"
            data-testid="quick-offer-strip"
          >
            <Sparkles className="shrink-0 text-amber-700" size={19} />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-extrabold text-amber-950">
                {offer.text || "Limited-time offer"}
              </p>
              <p className="font-mono text-sm font-extrabold text-amber-900">{offer.code}</p>
            </div>
            <button
              type="button"
              onClick={copyOffer}
              data-testid="copy-offer-code"
              className="inline-flex min-h-9 items-center gap-1 rounded-lg bg-amber-400 px-2.5 text-xs font-extrabold text-amber-950"
            >
              <Copy size={13} /> Copy
            </button>
          </section>
        )}

        <section className="px-3 pt-6" data-testid="quick-category-grid">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs font-bold text-emerald-700">SHOP BY CATEGORY</p>
              <h1 className="mt-1 text-xl font-extrabold text-slate-950">Everyday essentials</h1>
            </div>
            <Link
              to="/categories"
              data-testid="quick-see-all-categories"
              className="text-xs font-extrabold text-emerald-700"
            >
              See all →
            </Link>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2.5">
            {categoriesLoading && categories.length === 0 &&
              [...Array(8)].map((_, i) => (
                <div key={`cat-skel-${i}`} className="rounded-xl p-2" data-testid="category-skeleton">
                  <div className="aspect-square w-full animate-pulse rounded-lg bg-slate-200" />
                  <div className="mx-auto mt-1.5 h-3 w-3/4 animate-pulse rounded bg-slate-200" />
                </div>
              ))}
            {categories.slice(0, 8).map((category, index) => {
              const product = productsForCategory(category.key);
              return (
                <Link
                  key={category.key}
                  to={`/c/${category.key}`}
                  data-testid={`quick-category-${category.key}`}
                  className="overflow-hidden rounded-xl p-2 transition-transform duration-200 hover:-translate-y-0.5"
                  style={{ backgroundColor: categoryColours[index % categoryColours.length] }}
                >
                  {category.image || product?.image ? (
                    <img
                      src={category.image || product.image}
                      alt=""
                      width={160}
                      height={160}
                      loading="lazy"
                      decoding="async"
                      className="aspect-square w-full rounded-lg object-cover"
                    />
                  ) : (
                    <span className="grid aspect-square place-items-center rounded-lg bg-white/60 text-emerald-800">
                      <Grid2X2 size={22} />
                    </span>
                  )}
                  <span className="mt-1.5 block line-clamp-2 text-[10px] font-extrabold leading-tight text-slate-900">
                    {category.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="px-3 pb-5 pt-7" data-testid="quick-product-catalogue">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs font-bold text-emerald-700">DELIVERED FAST</p>
              <h2 className="mt-1 text-xl font-extrabold text-slate-950">Popular near you</h2>
            </div>
            <Link
              to="/c/all"
              data-testid="quick-see-all-products"
              className="text-xs font-extrabold text-emerald-700"
            >
              See all →
            </Link>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {productsLoading && products.length === 0 &&
              [...Array(6)].map((_, i) => (
                <div
                  key={`prod-skel-${i}`}
                  className="flex flex-col rounded-2xl border border-stone-200 bg-white p-3"
                  data-testid="product-skeleton"
                >
                  <div className="aspect-square w-full animate-pulse rounded-xl bg-slate-200" />
                  <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-200" />
                  <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-slate-200" />
                  <div className="mt-3 h-8 w-full animate-pulse rounded-lg bg-slate-200" />
                </div>
              ))}
            {products.slice(0, visibleCount).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          {visibleCount < products.length && (
            <div
              ref={loadMoreRef}
              data-testid="load-more-sentinel"
              className="py-6 text-center text-xs font-semibold text-slate-400"
            >
              Loading more…
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
